import { TRPCClientErrorLike } from '@trpc/client';
import { TRPCError } from '@trpc/server';
import { TRPC_ERROR_CODE_KEY } from '@trpc/server/rpc';
import { HttpStatusCode } from 'axios';
import { TFunction } from 'i18next';

import type { AppRouter } from '@sternum/api/trpc/router';

import { i18n, namespaces } from '../i18n';

const TranslationFileErrorsObjectPrefix = 'apiErrors';

export function makeErrorMessage(error: TRPCClientErrorLike<AppRouter>, t: TFunction = i18n.t): string {
  const fallbackMessage = error.message || t('generalError', { ns: namespaces.common });

  const translationDetails = getErrorTranslationDetails(error);

  if (!translationDetails) return fallbackMessage;

  const translatedMessage = t(translationDetails.key, { ns: translationDetails.ns });

  if (translatedMessage === translationDetails.key) {
    console.warn(`No translation was found for API error: ${error.data?.appCode}`);

    return fallbackMessage;
  }

  return translatedMessage;
}

export function getErrorTranslationDetails(error: TRPCClientErrorLike<AppRouter>) {
  if (!error.data?.appCode) return null;

  const [prefix, errorCode] = error.data.appCode.split('.');

  if (!prefix || !errorCode) {
    console.warn(`Failed to interpret error code: ${error.data.appCode}`);

    return null;
  }

  const ns = getTranslationNamespaceBasedOnPrefix(prefix);

  return { ns, key: `${TranslationFileErrorsObjectPrefix}.${errorCode}` };
}

export function convertHttpStatusCodeIntoTRPCError(statusCode: HttpStatusCode, message?: string) {
  const makeTRPCError = (code: TRPC_ERROR_CODE_KEY) => new TRPCError({ code, message });

  switch (statusCode) {
    case 400:
      return makeTRPCError('BAD_REQUEST');

    case 401:
      return makeTRPCError('UNAUTHORIZED');

    case 403:
      return makeTRPCError('FORBIDDEN');

    case 404:
      return makeTRPCError('NOT_FOUND');

    case 409:
      return makeTRPCError('CONFLICT');

    case 500:
    default:
      return makeTRPCError('INTERNAL_SERVER_ERROR');
  }
}

function getTranslationNamespaceBasedOnPrefix(prefix: string): keyof typeof namespaces {
  return namespaces[prefix as never] || namespaces.common;
}
